import { useNavigate, useLocation } from "react-router-dom";
import { Play, Pause, X, Waves } from "lucide-react";
import { useLibraryAudio } from "../contexts/LibraryAudioContext";

// Sticky mini-player shown across every page when a Daily Practice track is
// loaded — except on the dedicated player page itself, where it would be
// redundant with the in-page transport controls.

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export const LibraryMiniPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { practice, isPlaying, currentTime, duration, toggle, stop, bilateralOn } =
    useLibraryAudio();

  if (!practice) return null;
  // Suppress on the dedicated player page so the inline controls aren't doubled up.
  if (location.pathname.startsWith(`/library/${practice.id}`)) return null;

  const onOpenFull = () => navigate(`/library/${practice.id}`);
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,560px)]"
      data-testid="library-mini-player"
    >
      <div className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.16)] border border-slate-100 overflow-hidden">
        {/* progress strip */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-[#306090] transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="flex items-center gap-3 p-3">
          <button
            onClick={onOpenFull}
            className="shrink-0 w-12 h-12 rounded-xl bg-cover bg-center"
            style={{ backgroundImage: `url('${practice.image}')` }}
            aria-label="Open full player"
            data-testid="mini-player-open-btn"
          />
          <button
            type="button"
            className="flex-1 min-w-0 cursor-pointer text-left bg-transparent border-0 p-0"
            onClick={onOpenFull}
            aria-label={`Open ${practice.title}`}
            data-testid="mini-player-meta"
          >
            <p className="font-outfit font-semibold text-sm text-[#1B2349] truncate">
              {practice.title}
            </p>
            <div className="flex items-center gap-1.5 text-xs text-[#718096]">
              <span>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              {practice.bilateralCapable && bilateralOn && (
                <span className="inline-flex items-center gap-0.5 text-[#3090A8]">
                  <Waves className="w-3 h-3" />
                </span>
              )}
            </div>
          </button>

          <button
            onClick={toggle}
            className="shrink-0 w-10 h-10 rounded-full bg-[#1B2349] text-white flex items-center justify-center hover:bg-[#1A202C] transition-colors active:scale-95"
            aria-label={isPlaying ? "Pause" : "Play"}
            data-testid="mini-player-toggle-btn"
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </button>

          <button
            onClick={stop}
            className="shrink-0 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-[#718096] flex items-center justify-center transition-colors"
            aria-label="Stop and close"
            data-testid="mini-player-close-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LibraryMiniPlayer;
